import React, { useMemo } from "react";
import { Badge, Button, Form, Modal, Spinner } from "react-bootstrap";
import CloseBtn from "../CloseBtn";
import SimpleTable from "../../comman/custom-table/SimpleTable";

const OrdersMainModal = ({
  onHide,
  show,
  importSuccess,
  importReport,
  uploading,
  onImportFileChange,
  downloadValidationErrorsCsv,
  setShowImport,
  setImportReport,
  setImportSuccess,
  title = "Upload Orders",
  accept = ".xlsx,.xls,.csv",
}) => {
  const rows = useMemo(
    () =>
      (importReport?.errors || []).map((er, i) => ({
        id: `${er.row}-${i}`,
        serial: i + 1,
        row: er.row,
        errors: er.errors || [],
        data: er.data || {},
      })),
    [importReport]
  );

  const columns = useMemo(
    () => [
      {
        id: "serial",
        header: "Sr.",
        cell: (_v, row) => row.serial,
      },
      {
        id: "row",
        header: "Row",
        accessor: "row",
      },
      {
        id: "errors",
        header: "Errors",
        accessor: "errors",
        cell: (v) => (
          <ul className="mb-0 ps-3">
            {v.map((msg, idx) => (
              <li key={idx}>{msg}</li>
            ))}
          </ul>
        ),
      },
      {
        id: "data",
        header: "Data (original)",
        accessor: "data",
        cell: (v) => (
          <code className="small">{JSON.stringify(v || {}, null, 0)}</code>
        ),
      },
    ],
    []
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      size={!importSuccess && importReport ? "lg" : "md"}
      centered
      backdrop={uploading ? "static" : true}
      keyboard={!uploading}
    >
      {!uploading && <CloseBtn onClick={onHide} color="#bf352b" />}

      <Modal.Header>
        <Modal.Title>
          {importSuccess === true
            ? `${title} – Result`
            : importSuccess === false
            ? `${title} – Validation Errors`
            : title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {uploading ? (
          <div className="text-center my-4">
            <Spinner animation="border" role="status" />
            <div className="mt-2">Uploading… please wait</div>
          </div>
        ) : (
          <>
            {importSuccess === null && (
              <Form.Group className="mb-3">
                <Form.Label>Select file ({accept})</Form.Label>
                <Form.Control
                  type="file"
                  accept={accept}
                  disabled={uploading}
                  onChange={onImportFileChange}
                />
                <div className="text-muted small">
                  Ensure your columns match the server mapping (e.g.{" "}
                  <code>order_no</code>, <code>order_amount</code>,{" "}
                  <code>order_date</code>, <code>partner_code</code>, etc.).
                </div>
              </Form.Group>
            )}

            {importSuccess === true && importReport && (
              <div className="d-grid gap-3 columns-4">
                <Badge bg="secondary" className="p-2">
                  Total: {importReport?.totalRows ?? 0}
                </Badge>
                <Badge bg="success" className="p-2">
                  Created: {importReport?.createdCount ?? 0}
                </Badge>
                <Badge bg="info" className="p-2">
                  Updated: {importReport?.updatedCount ?? 0}
                </Badge>
                <Badge bg="danger" className="p-2">
                  Errors: {importReport?.errorCount ?? 0}
                </Badge>
              </div>
            )}

            {importSuccess === false && importReport && (
              <>
                <div className="d-grid gap-3 columns-4">
                  <Badge bg="secondary" className="p-2">
                    Total: {importReport?.totalRows ?? 0}
                  </Badge>
                  <Badge bg="success" className="p-2">
                    Valid: {importReport?.validRows ?? 0}
                  </Badge>
                  <Badge bg="danger" className="p-2">
                    Error Rows: {importReport?.errorCount ?? 0}
                  </Badge>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Validation Errors</h6>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => downloadValidationErrorsCsv(importReport)}
                  >
                    Download errors (.csv)
                  </Button>
                </div>

                <SimpleTable
                  data={rows}
                  columns={columns}
                  loading={false}
                  keyField="id"
                  responsive
                  size="sm"
                  emptyMessage="No errors found"
                  headerSticky
                  tableProps={{ bordered: true, className: "mb-0" }}
                />
              </>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {uploading ? (
          <Button variant="secondary" disabled>
            <Spinner animation="border" size="sm" className="me-2" />
            Uploading…
          </Button>
        ) : importSuccess === null ? (
          <Button
            variant="secondary"
            onClick={() => {
              setShowImport(false);
              setImportReport(null);
              setImportSuccess(null);
            }}
          >
            Close
          </Button>
        ) : (
          <>
            <Button
              variant="outline-secondary"
              onClick={() => {
                setImportReport(null);
                setImportSuccess(null);
              }}
            >
              Upload another file
            </Button>
            <Button variant="primary" onClick={onHide}>
              Done
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default OrdersMainModal;
